import {BehaviorSubject, distinctUntilChanged, Observable, skip} from 'rxjs';
import {
  Sound,
  SoundPlaybackResult,
  SoundSetting,
  WidgetNotificationsAPI,
} from '.';
import {
  ParentMessageBus,
  sendUnseenMessagesCount,
  sendWidgetInitialized,
} from '../iframe';

const SOUNDS: {
  [key in Sound]: HTMLAudioElement;
} = {
  'new-message': new Audio(
    'https://b.stripecdn.com/content/new-message-sound.mp3',
  ),
};

export async function playSound(name: Sound) {
  await SOUNDS[name].play();
}

export class DefaultNotifications implements WidgetNotificationsAPI {
  private soundSetting$: BehaviorSubject<SoundSetting>;

  constructor(initialSoundSetting: SoundSetting = 'disabled') {
    this.soundSetting$ = new BehaviorSubject<SoundSetting>(initialSoundSetting);
  }

  get soundSetting(): SoundSetting {
    return this.soundSetting$.value;
  }

  setSoundSetting = (soundSetting: SoundSetting): void => {
    this.soundSetting$.next(soundSetting);
  };

  soundSettingObservable = (): Observable<SoundSetting> => {
    return this.soundSetting$.asObservable().pipe(
      // only emit distinct changes; no back-to-back repeats
      distinctUntilChanged(),
      // skip(1) skips initial value; only emit changes
      skip(1),
    );
  };

  playSound = async (name: Sound): Promise<SoundPlaybackResult> => {
    try {
      if (this.soundSetting === 'on') {
        await playSound(name);
        return 'played';
      } else {
        return 'muted';
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        return 'errorNotAllowed';
      } else if (
        err instanceof DOMException &&
        err.name === 'errorNotSupported'
      ) {
        return 'errorNotSupported';
      } else {
        return 'errorOther';
      }
    }
  };

  setUnseenMessagesCount = (_count: number): void => {
    // not implemented
  };

  clearUnseenMessagesCount = (): void => {
    // not implemented
  };
}

export class ParentNotifications
  extends DefaultNotifications
  implements WidgetNotificationsAPI
{
  private parentBus: ParentMessageBus;

  constructor({
    initialSoundSetting = 'disabled',
    parentBus,
  }: {
    initialSoundSetting?: SoundSetting;
    parentBus: ParentMessageBus;
  }) {
    super(initialSoundSetting);
    this.parentBus = parentBus;
    sendWidgetInitialized(parentBus);
  }

  setUnseenMessagesCount = (count: number): void => {
    sendUnseenMessagesCount(this.parentBus, count);
  };

  clearUnseenMessagesCount = (): void => {
    sendUnseenMessagesCount(this.parentBus, 0);
  };
}
